import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import axios from 'axios';
import { signupCreditor } from '../services/api';
import { useAuthStore } from '../store/authStore';

const EASE = [0.2, 0, 0, 1] as const;

const formContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.05,
    },
  },
};

const formChild: Variants = {
  hidden: { opacity: 0, y: 6 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: EASE },
  },
};

/**
 * Public creditor self-signup. Reached via `/signup-creditor` — no
 * `ProtectedRoute`. Creates a lender row + admin user in one shot;
 * the lender lands in `review_status: "pending_review"` until an
 * operator approves. We auto-sign-in the new admin so the SPA can
 * drop them straight onto the pending-approval screen.
 *
 * Aesthetic mirrors `OpenEntryPage` / `SignupPage`: editorial
 * "ledger directory" — section headers, hairline rules, password
 * eye toggle + Caps Lock detector.
 */
export default function CreditorSignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setToken = useAuthStore((s) => s.setToken);
  const setUser = useAuthStore((s) => s.setUser);

  // §I — Långiver fields
  const [displayName, setDisplayName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  // §II — Adminbruger fields
  const [adminName, setAdminName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePasswordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const native = e.nativeEvent as unknown as {
      getModifierState?: (k: string) => boolean;
    };
    if (typeof native.getModifierState === 'function') {
      setCapsLockOn(native.getModifierState('CapsLock'));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (adminPassword.length < 8) {
      setError(t('creditorSignup.passwordTooShort'));
      return;
    }
    if (adminPassword !== adminPasswordConfirm) {
      setError(t('creditorSignup.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await signupCreditor({
        display_name: displayName.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim() || undefined,
        admin_name: adminName.trim() || undefined,
        admin_password: adminPassword,
      });
      // Drop the freshly-issued credentials into the auth store —
      // same fields `loginRequest` populates so the rest of the SPA
      // can't tell the difference between "logged in via /login" and
      // "logged in via creditor self-signup".
      setToken(response.access_token);
      setUser(response.user);
      // The new admin lands on the pending-approval screen, which
      // shows a calm "we'll be in touch" message until an operator
      // approves the lender.
      navigate('/pending-approval', { replace: true });
    } catch (err) {
      // Backend contract: 409 with `error_code: "email_already_exists"`
      // for both lender-collision + user-collision branches. Surface
      // a specific message; everything else falls through to the
      // generic envelope.
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data as
          | { detail?: { error_code?: string; detail?: string } }
          | undefined;
        if (detail?.detail?.error_code === 'email_already_exists') {
          setError(t('creditorSignup.errorEmailExists'));
        } else {
          setError(t('creditorSignup.errorGeneric'));
        }
      } else {
        setError(t('creditorSignup.errorGeneric'));
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-12 max-w-[440px]">
      <motion.header
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE }}
      >
        <div className="section-label mb-3">{t('auth.regOfficeLabel')}</div>
        <h1 className="font-mono text-2xl tracking-tight text-[var(--color-ink)]">
          {t('creditorSignup.title')}
        </h1>
        <p className="mt-3 text-sm text-[var(--color-ink-soft)] leading-relaxed">
          {t('creditorSignup.lede')}
        </p>
      </motion.header>

      <motion.form
        onSubmit={handleSubmit}
        className="space-y-12"
        variants={formContainer}
        initial="hidden"
        animate="visible"
        noValidate
      >
        {/* § I — Långiver */}
        <motion.section variants={formChild} className="rule pt-8 space-y-6">
          <div className="section-label">
            {t('creditorSignup.sectionLender')}
          </div>

          <div>
            <label
              htmlFor="creditor-display-name"
              className="section-label block mb-2"
            >
              {t('creditorSignup.displayNameLabel')}
              <span className="ml-1 text-[var(--color-ink)]">*</span>
            </label>
            <input
              id="creditor-display-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t('creditorSignup.displayNamePlaceholder')}
              autoComplete="organization"
              className="w-full"
              required
              minLength={2}
              maxLength={200}
              disabled={submitting}
            />
          </div>

          <div>
            <label
              htmlFor="creditor-contact-email"
              className="section-label block mb-2"
            >
              {t('creditorSignup.contactEmailLabel')}
              <span className="ml-1 text-[var(--color-ink)]">*</span>
            </label>
            <input
              id="creditor-contact-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              autoComplete="email"
              spellCheck={false}
              className="w-full"
              required
              maxLength={320}
              disabled={submitting}
            />
          </div>

          <div>
            <label
              htmlFor="creditor-contact-phone"
              className="section-label block mb-2"
            >
              {t('creditorSignup.contactPhoneLabel')}
            </label>
            <input
              id="creditor-contact-phone"
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder={t('creditorSignup.contactPhonePlaceholder')}
              autoComplete="tel"
              className="w-full"
              maxLength={64}
              disabled={submitting}
            />
          </div>
        </motion.section>

        {/* § II — Adminbruger */}
        <motion.section variants={formChild} className="rule pt-8 space-y-6">
          <div className="section-label">
            {t('creditorSignup.sectionAdmin')}
          </div>

          <div>
            <label
              htmlFor="creditor-admin-name"
              className="section-label block mb-2"
            >
              {t('creditorSignup.adminNameLabel')}
            </label>
            <input
              id="creditor-admin-name"
              type="text"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              autoComplete="name"
              className="w-full"
              maxLength={200}
              disabled={submitting}
            />
          </div>

          <div>
            <label
              htmlFor="creditor-admin-password"
              className="section-label block mb-2"
            >
              {t('creditorSignup.adminPasswordLabel')}
              <span className="ml-1 text-[var(--color-ink)]">*</span>
            </label>
            <div className="relative w-full">
              <input
                id="creditor-admin-password"
                type={passwordVisible ? 'text' : 'password'}
                value={adminPassword}
                onChange={(e) => {
                  setAdminPassword(e.target.value);
                  if (error) setError(null);
                }}
                onBlur={() => setCapsLockOn(false)}
                onKeyDown={handlePasswordKeyDown}
                onKeyUp={handlePasswordKeyDown}
                autoComplete="new-password"
                spellCheck={false}
                className="w-full pr-10"
                required
                minLength={8}
                disabled={submitting}
              />
              <button
                type="button"
                onClick={() => setPasswordVisible((v) => !v)}
                aria-label={
                  passwordVisible
                    ? t('auth.hidePassword')
                    : t('auth.showPassword')
                }
                aria-pressed={passwordVisible}
                tabIndex={-1}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] transition-colors"
              >
                {passwordVisible ? (
                  <EyeOff className="w-4 h-4" strokeWidth={1.75} />
                ) : (
                  <Eye className="w-4 h-4" strokeWidth={1.75} />
                )}
              </button>
            </div>
            <AnimatePresence>
              {capsLockOn && (
                <motion.div
                  key="caps-lock"
                  initial={{ opacity: 0, y: -2 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="mt-1.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-alert)]"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-[var(--color-alert)]" />
                  {t('auth.capsLockOn')}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div>
            <label
              htmlFor="creditor-admin-password-confirm"
              className="section-label block mb-2"
            >
              {t('creditorSignup.adminPasswordConfirmLabel')}
              <span className="ml-1 text-[var(--color-ink)]">*</span>
            </label>
            <input
              id="creditor-admin-password-confirm"
              type={passwordVisible ? 'text' : 'password'}
              value={adminPasswordConfirm}
              onChange={(e) => {
                setAdminPasswordConfirm(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="new-password"
              spellCheck={false}
              className="w-full"
              required
              minLength={8}
              disabled={submitting}
            />
          </div>
        </motion.section>

        <motion.div variants={formChild} className="flex items-center gap-3">
          <motion.button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            animate={
              submitting ? { opacity: [1, 0.7, 1] } : { opacity: 1 }
            }
            transition={
              submitting
                ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0.15 }
            }
          >
            <LogIn className="w-4 h-4" strokeWidth={1.5} />
            {submitting
              ? t('creditorSignup.submitting')
              : t('creditorSignup.submit')}
          </motion.button>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              role="alert"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -2 }}
              transition={{ duration: 0.22, ease: EASE }}
              className="border border-[var(--color-alert)] bg-[var(--color-alert-soft)] px-4 py-3"
            >
              <span className="font-mono text-xs text-[var(--color-alert)] uppercase tracking-wider">
                {error}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.form>

      <div className="text-xs font-mono text-[var(--color-ink-muted)]">
        {t('creditorSignup.alreadyHaveAccount')}{' '}
        <Link
          to="/login"
          className="text-[var(--color-primary)] hover:text-[var(--color-primary-strong)]"
        >
          {t('creditorSignup.signInLink')}
        </Link>
      </div>
    </div>
  );
}
